import React, { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useHistory, useParams } from "react-router-dom";
import { GoPrimitiveDot } from "react-icons/go";
import Swal from "sweetalert2";
import { useMutation, useQuery } from "@apollo/react-hooks";
import { APP_CONTEXT } from "../../../utils/context";
import PageHeader from "../../../components/PageHeader/PageHeader.component";
import { Row, Column } from "../../../components/Grid/Grid.component";
import Form from "./ContactInformation.form.component";
import Button from "../../../components/Button/Button.component";
import Error from "../../../components/Error/ErrorMessage.component";
import BorderSelect from "../../../components/Form/BorderSelect/BorderSelect.component";
import Notification from "../../../components/Toast/Toast.component";

// GQL
import {
  UPDATE_CONTACT_INFORMATION,
  DELETE_CONTACT_INFORMATION,
} from "../../../graphql/mutations/contactInformation.mutation";
import { GET_CONTACT_INFORMATION } from "../../../graphql/queries/contactInformation.query";

import { formatGraphqlError } from "../../../utils/api";

const ContactInformationEdit = () => {
  const { setActiveSideNav, setPageTitle } = useContext(APP_CONTEXT);
  const [contactInformationData, setContactInformationData] = useState({
    id: "",
    name: "",
    options: [],
    description: "",
    status: "ACTIVE",
  });
  const [updateContactInformation, { loading }] = useMutation(
    UPDATE_CONTACT_INFORMATION
  );
  const [deleteContactInformation] = useMutation(DELETE_CONTACT_INFORMATION);
  const { id } = useParams();
  const expenseClaim = useQuery(GET_CONTACT_INFORMATION, {
    fetchPolicy: "no-cache",
    variables: { id },
  });

  const { data } = expenseClaim;

  const history = useHistory();

  useEffect(() => {
    setActiveSideNav("cm");
    setPageTitle("Manage Contacts");
  });

  const {
    register,
    handleSubmit,
    errors,
    setValue,
    getValues,
    control,
  } = useForm();

  const changeStatus = (e) => {
    const tempContactInformationData = { ...contactInformationData };
    tempContactInformationData.status = e.target.value;
    setContactInformationData(tempContactInformationData);
  };

  const getColor = () => {
    switch (contactInformationData.status) {
      case "ACTIVE":
        return "#39C568";
      case "ARCHIVE":
        return "#FF003A";
      case "DRAFT":
        return "#707172";
      default:
        return "#707172";
    }
  };

  const onSubmit = (values) => {
    /**
     * Create new ContactInformation
     * push to list all contactInformations
     */

    const submitValues = values;
    submitValues.id = contactInformationData.id;
    updateContactInformation({
      variables: {
        ...submitValues,
      },
    })
      .then(() => {
        Notification.success({
          title: "Saved successfully",
          message: "You updated a Contacts",
        });
        history.push("/app/cms/contact-information");
      })
      .catch((err) =>
        Notification.error({
          title: "Update Contacts Failed",
          message: formatGraphqlError(err),
        })
      );
  };

  const handleDelete = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.value) {
        deleteContactInformation({
          variables: {
            id: contactInformationData.id,
          },
        })
          .then(() => {
            Swal.fire(
              "Deleted!",
              "ContactInformation has been deleted.",
              "success"
            ).then(() => {
              history.push("/app/cms/contact-information");
            });
          })
          .catch((err) =>
            Notification.error({
              title: "Delete ContactInformation Failed",
              message: formatGraphqlError(err),
            })
          );
      }
    });
  };

  if (expenseClaim.loading) {
    return "Loading...";
  }

  return (
    <>
      <PageHeader
        title={`Edit Contact Information: ${contactInformationData.name}`}
        back
      />
      <Row>
        <Column width="70%" noPad>
          <Form
            register={register}
            errors={errors}
            contactInformationData={contactInformationData}
            setContactInformationData={setContactInformationData}
            contactInformationEditData={data && data.contactInformation}
            setValue={setValue}
            getValues={getValues}
            control={control}
          />
        </Column>
        <Column width="30%">
          <BorderSelect
            header="Status"
            name="status"
            label="Please select a status"
            value={contactInformationData.status}
            register={register}
            onChange={changeStatus}
            options={[
              { value: "ACTIVE", text: "Active" },
              { value: "ARCHIVE", text: "Archive" },
              { value: "DRAFT", text: "Draft" },
            ]}
            icon={
              <GoPrimitiveDot style={{ color: getColor(), fontSize: "25px" }} />
            }
            margin="60px 0px 120px 0px"
            required={{
              required: "please enter a ContactInformation ID",
              pattern: {
                value: /^[a-zA-Z]+$/,
                message: "field cannot be empty",
              },
            }}
            error={errors.id && <Error message={errors.id.message} />}
          />

          <Button
            width="100%"
            onClick={() => handleDelete()}
            type="button"
            value="Archive"
            loading={loading}
          />
          <Button
            width="100%"
            onClick={handleSubmit(onSubmit)}
            type="button"
            kind="secondary"
            value="Save & Update"
            loading={loading}
          />
        </Column>
      </Row>
    </>
  );
};

export default ContactInformationEdit;
